'use client';

import React from 'react';
import {
  IRegionCam, IRegionFeaturedResort, IRegionRamResponse, IRegionShort,
} from '@/api/region/types';
import {
  IRelatedNews, OpenFlag, IResortPathInfo, IDescriptionsResort, IResortPageSnowInfo,
} from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useOTSTranslation, useMustacheTranslation } from '@/i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { useRegionLayoutBubble } from '@/page-components/region/_context';
import { IRegionLayout } from '@/page-components/region/_layout';
import { Routes } from '@/routes';
import PlanYourTrip from '@/components/common/PlanYourTrip/RegionWidget';
import { isMainLocale } from '@/util/i18nFlags';
import { Hero400Error } from '@/components/400';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { PageBody } from '@/components/common/PageBody';
import { ResortWebcam } from '@/components/common/ResortWebcam';
import { SeoSection } from '@/components/common/SeoSection';
import { Text } from '@/components/ui/text';
import { formatAgoTimestampOrString } from '@/util/formatAgo';
import { LoadMoreRows } from '@/components/region/LoadMoreRows';
import { useLoadMoreBuckets } from '@/components/region/useLoadMoreBuckets';
import { fetchRegionCamerasByStatus } from '@/api/region/static';

export type RegionCamsPageProps = {
  cameras: Record<OpenFlag, IRegionRamResponse>,
  featuredCam: IRegionCam,
  numResorts: number,
  featured: IRegionFeaturedResort,
  regionResortListGuide: Array<IResortPathInfo>,
  pathInfo: IRegionShort,
  relatedNews: IRelatedNews | null,
  featuredResortByIndex: IResortPageSnowInfo | null,
  featuredResortDescriptionTabs?: Array<IDescriptionsResort> | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-cams-page';
export const TEMPLATE_ID = '2.1.2';
export const TEMPLATE_NAME = 'Region Cams';

export const REGION_CAM_PAGE = 48;

// Order the OpenFlag buckets so open resorts surface first. The flat
// v3 grid drops the per-bucket section headings the legacy layout
// used; the ordering here is what preserves "open cams come first."
const STATUS_ORDER: OpenFlag[] = [
  OpenFlag.Open,
  OpenFlag.WeekendsOnly,
  OpenFlag.TemporarilyClosed,
  OpenFlag.NoReportAvailable,
  OpenFlag.Closed,
  OpenFlag.PermanentlyClosed,
];

export default function RegionCamsPage({
  cameras, pathInfo, numResorts, relatedNews,
}: RegionCamsPageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  useRegionLayoutBubble({});
  const showLodgingWidget = isMainLocale();

  // The 6 status buckets are fetched separately but read as one grid, so the
  // hook flattens them in priority order and Load More fills the earliest
  // incomplete bucket. Region 476 (Europe) has 267 webcams in its closed
  // bucket alone against a 48-per-page fetch.
  const fetchCamPage = React.useCallback(
    (flag: OpenFlag, page: number) => fetchRegionCamerasByStatus(
      pathInfo.uuid, flag, page, REGION_CAM_PAGE,
    ),
    [pathInfo.uuid],
  );

  const {
    rows: orderedCams, hasMore, isLoading, loadMore,
  } = useLoadMoreBuckets<OpenFlag, IRegionCam>({
    order: STATUS_ORDER,
    initial: cameras ?? {},
    fetchPage: fetchCamPage,
    pageSize: REGION_CAM_PAGE,
    label: `region ${pathInfo.uuid} cams`,
  });

  if (!cameras) {
    return <Hero400Error light />;
  }

  const totalCount = orderedCams.length;

  const { isLogical } = pathInfo;
  const seoTitle = isLogical ? t('region.cams.logicSeoTitle', { region: pathInfo.title })
    : t('region.cams.seoTitle', { region: pathInfo.title });
  const seoText = isLogical ? m('region.cams.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  }) : m('region.cams.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  });

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={seoTitle}>
            {seoText}
          </SeoSection>

          {totalCount > 0 ? (
            <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 xl:grid-cols-3">
              {orderedCams.map((cam) => (
                <ResortWebcam
                  key={`${cam.resort.uuid}-${cam.webcam.uuid}`}
                  showResort
                  resortName={cam.resort.title}
                  caption={cam.webcam.title}
                  image={cam.webcam.image}
                  timestamp={formatAgoTimestampOrString(cam.webcam.date)}
                  href={Routes.resortCams(cam.resort)}
                />
              ))}
            </div>
          ) : (
            <Text variant="muted">
              {t('common.na')}
            </Text>
          )}

          <LoadMoreRows hasMore={hasMore} isLoading={isLoading} onLoadMore={loadMore} />

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={pathInfo.title}
          />

          {showLodgingWidget && (
            <PlanYourTrip
              hasSkiRental={false}
              hasCarRental={false}
              hasFlights={false}
              region={pathInfo}
              isPartner={false}
            />
          )}
        </PageBody>
      </Main>

      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
